import React, { useState } from 'react';
import { Mail, Shield, CheckCircle, AlertCircle, Phone } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import clientSelfServiceService from '../../../services/clientSelfServiceService';

const AccountRecoveryWorkflow = () => {
  const [step, setStep] = useState('verify'); // verify, method, success
  const [email, setEmail] = useState('');
  const [verificationType, setVerificationType] = useState('email'); // email, phone, security
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [accountInfo, setAccountInfo] = useState(null);
  const [attemptCount, setAttemptCount] = useState(0);
  const maxAttempts = 5;

  const handleVerifyAccount = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccess('');

    if (attemptCount >= maxAttempts) {
      setError('Maximum verification attempts reached. Please contact support.');
      return;
    }

    if (!email?.trim()) {
      setError('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    const result = await clientSelfServiceService?.verifyAccountExists(email);
    setLoading(false);

    if (result?.exists) {
      setAccountInfo({
        companyName: result?.companyName,
        isActive: result?.isActive
      });
      setStep('method');
      setAttemptCount(0);
    } else {
      setAttemptCount(prev => prev + 1);
      setError('No account found with this email address. Please check and try again.');
    }
  };

  const handleSendRecovery = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    const result = await clientSelfServiceService?.sendAccountRecoveryEmail(email, verificationType);
    setLoading(false);

    if (result?.success) {
      setSuccess(result?.message);
      setStep('success');
    } else {
      setError(result?.message || 'Failed to send recovery email. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Verify Account Step */}
      {step === 'verify' && (
        <div className="space-y-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-blue-600 dark:text-blue-400 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-1">Account Recovery</h4>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Recover access to your locked or suspended account. Enter your registered email to begin the verification process.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleVerifyAccount} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Company Email Address *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  placeholder="company@example.com"
                  className="pl-10"
                  disabled={loading}
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <AlertCircle size={16} className="text-red-600 dark:text-red-400" />
                  <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                </div>
              </div>
            )}

            <Button
              type="submit"
              variant="default"
              className="w-full"
              disabled={loading || attemptCount >= maxAttempts}
            >
              {loading ? 'Verifying...' : 'Verify Account'}
            </Button>

            {attemptCount > 0 && attemptCount < maxAttempts && (
              <p className="text-xs text-muted-foreground text-center">
                Attempts remaining: {maxAttempts - attemptCount}
              </p>
            )}
          </form>

          <div className="border-t border-border pt-4">
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Shield size={16} className="text-amber-600 dark:text-amber-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    <strong>Security Notice:</strong> Account recovery requires verification of your identity. Suspicious activity is monitored and may result in temporary account suspension.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Verification Method Selection Step */}
      {step === 'method' && (
        <div className="space-y-6">
          <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <CheckCircle size={20} className="text-emerald-600 dark:text-emerald-400 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-emerald-900 dark:text-emerald-100 mb-1">Account Found</h4>
                <p className="text-sm text-emerald-700 dark:text-emerald-300">
                  Company: <strong>{accountInfo?.companyName}</strong>
                </p>
                <p className="text-sm text-emerald-700 dark:text-emerald-300">
                  Status: <strong>{accountInfo?.isActive ? 'Active' : 'Inactive'}</strong>
                </p>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-foreground mb-3">Select Verification Method</h4>
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setVerificationType('email')}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                  verificationType === 'email' ?'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' :'border-border hover:border-emerald-300 dark:hover:border-emerald-700'
                }`}
              >
                <div className="flex items-start gap-3">
                  <Mail size={20} className="text-emerald-600 dark:text-emerald-400 mt-0.5" />
                  <div className="flex-1">
                    <h5 className="font-medium text-foreground mb-1">Email Verification</h5>
                    <p className="text-sm text-muted-foreground">
                      Receive a secure recovery link via email to {email}
                    </p>
                  </div>
                  {verificationType === 'email' && (
                    <CheckCircle size={20} className="text-emerald-600 dark:text-emerald-400" />
                  )}
                </div>
              </button>

              <button
                type="button"
                onClick={() => setVerificationType('phone')}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                  verificationType === 'phone' ?'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' :'border-border hover:border-emerald-300 dark:hover:border-emerald-700'
                }`}
                disabled
              >
                <div className="flex items-start gap-3">
                  <Phone size={20} className="text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <h5 className="font-medium text-muted-foreground mb-1">Phone Verification</h5>
                    <p className="text-sm text-muted-foreground">
                      Coming soon - SMS verification to registered phone number
                    </p>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setVerificationType('security')}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                  verificationType === 'security' ?'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' :'border-border hover:border-emerald-300 dark:hover:border-emerald-700'
                }`}
                disabled
              >
                <div className="flex items-start gap-3">
                  <Shield size={20} className="text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <h5 className="font-medium text-muted-foreground mb-1">Security Questions</h5>
                    <p className="text-sm text-muted-foreground">
                      Coming soon - Answer security questions to verify identity
                    </p>
                  </div>
                </div>
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <AlertCircle size={16} className="text-red-600 dark:text-red-400" />
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setStep('verify');
                setError('');
                setSuccess('');
              }}
              className="flex-1"
              disabled={loading}
            >
              Back
            </Button>
            <Button
              variant="default"
              onClick={handleSendRecovery}
              className="flex-1"
              disabled={loading || verificationType !== 'email'}
            >
              {loading ? 'Sending...' : 'Send Recovery Link'}
            </Button>
          </div>
        </div>
      )}

      {/* Success Step */}
      {step === 'success' && (
        <div className="space-y-6 text-center">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
              <CheckCircle size={32} className="text-emerald-600 dark:text-emerald-400" />
            </div>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-foreground mb-2">Recovery Email Sent!</h3>
            <p className="text-muted-foreground mb-4">
              We've sent account recovery instructions to <strong>{email}</strong>. Please check your inbox and spam folder.
            </p>
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-left">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                <strong>Next Steps:</strong>
              </p>
              <ul className="text-sm text-blue-700 dark:text-blue-300 list-disc list-inside mt-2 space-y-1">
                <li>Check your email for the recovery link</li>
                <li>Click the link to verify your identity</li>
                <li>Follow instructions to regain account access</li>
                <li>Link expires in 1 hour for security</li>
              </ul>
            </div>
          </div>
          <Button
            variant="default"
            onClick={() => {
              setStep('verify');
              setEmail('');
              setError('');
              setSuccess('');
            }}
            className="w-full"
          >
            Start New Recovery
          </Button>
        </div>
      )}
    </div>
  );
};

export default AccountRecoveryWorkflow;